import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Check, X, Tags, Palette, Loader2, Bug, RefreshCw, Truck, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ICON_OPTIONS = [
  "shield", "shield-check", "cable", "plug-zap", "battery-charging",
  "headphones", "watch", "tablet", "phone", "baby", "sparkles",
  "apple", "smartphone", "package",
];

export default function AdminSettings() {
  const [tab, setTab] = useState("categories");

  return (
    <div data-testid="admin-settings">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Shop-Einstellungen</div>
        <h1 className="font-display text-3xl font-bold text-white mt-1">Kategorien, Farben & Versand</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Hier kannst du Kategorien, Farben und die Versandpreise jederzeit selbst anpassen. Änderungen sind sofort im Shop aktiv.
        </p>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 p-1 bg-[#141417] rounded-xl border border-white/5 w-fit">
        <TabButton active={tab === "categories"} onClick={() => setTab("categories")} icon={Tags} label="Kategorien" testid="tab-categories" />
        <TabButton active={tab === "colors"} onClick={() => setTab("colors")} icon={Palette} label="Farben" testid="tab-colors" />
        <TabButton active={tab === "shipping"} onClick={() => setTab("shipping")} icon={Truck} label="Versand" testid="tab-shipping" />
        <TabButton active={tab === "errors"} onClick={() => setTab("errors")} icon={Bug} label="Fehlerberichte" testid="tab-errors" />
      </div>

      <div className="mt-6">
        {tab === "categories" ? <CategoriesPanel />
          : tab === "colors" ? <ColorsPanel />
          : tab === "shipping" ? <ShippingPanel />
          : <ErrorsPanel />}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
        active ? "bg-[#DC1928] text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

/* ───────── Categories ───────── */
function CategoriesPanel() {  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ id: "", name: "", icon: "package", sort_order: 100 });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/categories");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    const maxSort = items.reduce((m, it) => Math.max(m, it.sort_order || 0), 0);
    setForm({ id: "", name: "", icon: "package", sort_order: maxSort + 10 });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({ ...it });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Bitte einen Namen eingeben");
      return;
    }
    try {
      if (editing) {
        await api.patch(`/admin/categories/${editing}`, { ...form, id: editing });
        toast.success("Kategorie aktualisiert");
      } else {
        await api.post("/admin/categories", { ...form, id: form.id || form.name });
        toast.success("Kategorie erstellt");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const del = async (id) => {
    if (!window.confirm(`Kategorie wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      toast.success("Kategorie gelöscht");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-zinc-500">{items.length} Kategorie{items.length === 1 ? "" : "n"}</div>
        <Button onClick={openCreate} className="bg-[#DC1928] hover:bg-[#B01829] rounded-full text-white" data-testid="add-category-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Neue Kategorie
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-3 p-4 bg-[#141417] rounded-xl border border-white/5 hover:border-white/15 transition-colors" data-testid={`category-row-${it.id}`}>
              <div className="w-10 h-10 rounded-lg bg-[#0A0A0B] border border-white/5 flex items-center justify-center text-zinc-500 text-[10px] uppercase">{it.icon || "—"}</div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-white truncate">{it.name}</div>
                <div className="text-[10px] text-zinc-500 truncate">ID: {it.id} · Sort: {it.sort_order}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => openEdit(it)} className="text-zinc-400 hover:text-white hover:bg-white/5" data-testid={`edit-category-${it.id}`}><Edit className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => del(it.id)} className="hover:bg-white/5" data-testid={`del-category-${it.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[#141417] border-white/10 text-white" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{editing ? "Kategorie bearbeiten" : "Neue Kategorie"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">ID wird automatisch aus dem Namen generiert.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0B] border-white/10 text-white" data-testid="category-form-name" />
            </div>
            <div>
              <Label className="text-zinc-400">Icon (Lucide-Name)</Label>
              <div className="grid grid-cols-7 gap-1.5 mt-1.5">
                {ICON_OPTIONS.map((ic) => (
                  <button
                    key={ic}
                    type="button"
                    onClick={() => setForm({ ...form, icon: ic })}
                    title={ic}
                    className={`aspect-square rounded-md border text-[9px] uppercase ${form.icon === ic ? "border-[#DC1928] bg-[#DC1928]/10 text-white" : "border-white/10 bg-[#0A0A0B] text-zinc-500 hover:border-white/30"}`}
                  >
                    {ic.slice(0, 3)}
                  </button>
                ))}
              </div>
              <Input
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                placeholder="oder custom Icon-Name"
                className="bg-[#0A0A0B] border-white/10 text-white mt-2 text-xs"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Sortierung (kleinere Werte zuerst)</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
                className="bg-[#0A0A0B] border-white/10 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white">Abbrechen</Button>
            <Button onClick={save} className="bg-[#DC1928] hover:bg-[#B01829] text-white" data-testid="category-form-save">
              {editing ? "Speichern" : "Erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ───────── Colors ───────── */
function ColorsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ id: "", name: "", hex: "#000000", sort_order: 100 });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/colors");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    const maxSort = items.reduce((m, it) => Math.max(m, it.sort_order || 0), 0);
    setForm({ id: "", name: "", hex: "#1E40AF", sort_order: maxSort + 10 });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({ ...it });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Bitte einen Namen eingeben");
      return;
    }
    try {
      if (editing) {
        await api.patch(`/admin/colors/${editing}`, { ...form, id: editing });
        toast.success("Farbe aktualisiert");
      } else {
        await api.post("/admin/colors", { ...form, id: form.id || form.name });
        toast.success("Farbe erstellt");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const del = async (id) => {
    if (!window.confirm(`Farbe wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/colors/${id}`);
      toast.success("Farbe gelöscht");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-zinc-500">{items.length} Farbe{items.length === 1 ? "" : "n"}</div>
        <Button onClick={openCreate} className="bg-[#DC1928] hover:bg-[#B01829] rounded-full text-white" data-testid="add-color-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Neue Farbe
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-3 p-4 bg-[#141417] rounded-xl border border-white/5 hover:border-white/15 transition-colors" data-testid={`color-row-${it.id}`}>
              <span className="w-8 h-8 rounded-full border-2 border-white/15 shrink-0" style={{ background: it.hex === "transparent" ? "repeating-conic-gradient(#444 0% 25%, #aaa 0% 50%) 50% / 8px 8px" : it.hex }} />
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-white truncate">{it.name}</div>
                <div className="text-[10px] text-zinc-500 font-mono truncate">{it.hex}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => openEdit(it)} className="text-zinc-400 hover:text-white hover:bg-white/5" data-testid={`edit-color-${it.id}`}><Edit className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" onClick={() => del(it.id)} className="hover:bg-white/5" data-testid={`del-color-${it.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[#141417] border-white/10 text-white" data-testid="color-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{editing ? "Farbe bearbeiten" : "Neue Farbe"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z.B. Mintgrün" className="bg-[#0A0A0B] border-white/10 text-white" data-testid="color-form-name" />
            </div>
            <div>
              <Label className="text-zinc-400">Hex-Farbe</Label>
              <div className="flex items-center gap-3 mt-1.5">
                <input
                  type="color"
                  value={form.hex.startsWith("#") ? form.hex : "#1E40AF"}
                  onChange={(e) => setForm({ ...form, hex: e.target.value })}
                  className="w-14 h-10 rounded-lg bg-transparent border border-white/10 cursor-pointer"
                  data-testid="color-form-picker"
                />
                <Input
                  value={form.hex}
                  onChange={(e) => setForm({ ...form, hex: e.target.value })}
                  placeholder="#1E40AF oder transparent"
                  className="bg-[#0A0A0B] border-white/10 text-white font-mono"
                />
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">Tipp: "transparent" für Klar-Look (Karo-Muster).</div>
            </div>
            <div>
              <Label className="text-zinc-400">Sortierung</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
                className="bg-[#0A0A0B] border-white/10 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white">Abbrechen</Button>
            <Button onClick={save} className="bg-[#DC1928] hover:bg-[#B01829] text-white" data-testid="color-form-save">
              {editing ? "Speichern" : "Erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ───────── Frontend crash reports ───────── */
function ErrorsPanel() {  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/client-errors?limit=100");
      setRows(r.data || []);
    } catch {
      toast.error("Fehlerberichte konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="errors-panel">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-zinc-500 max-w-2xl">
          Hier landen automatisch alle Abstürze, die Kunden im Browser erleben (z. B. leerer/schwarzer Bildschirm).
          Schick mir bei einer Kundenmeldung einfach den obersten Eintrag — daraus sehe ich die genaue Ursache.
        </p>
        <Button onClick={load} variant="outline" className="shrink-0 bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white" data-testid="errors-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> Aktualisieren
        </Button>
      </div>

      {loading ? (
        <div className="mt-8 flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>
      ) : rows.length === 0 ? (
        <div className="mt-8 p-8 rounded-2xl bg-[#141417] border border-white/5 text-center" data-testid="errors-empty">
          <Bug className="w-8 h-8 text-zinc-600 mx-auto" />
          <div className="text-white font-medium mt-3">Keine Fehlerberichte</div>
          <div className="text-sm text-zinc-500 mt-1">Aktuell sind keine Browser-Abstürze von Kunden gemeldet.</div>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {rows.map((e) => (
            <div key={e.id} className="p-4 rounded-2xl bg-[#141417] border border-white/5" data-testid={`error-row-${e.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                <span className="px-2 py-0.5 rounded-full bg-[#DC1928]/15 border border-[#DC1928]/30 text-[#FF8A95] font-medium">
                  {new Date(e.created_at).toLocaleString("de-AT")}
                </span>
                <span className="truncate max-w-[280px]">{e.url}</span>
              </div>
              <div className="text-sm text-white mt-2 font-mono break-words">{e.message}</div>
              <div className="text-[11px] text-zinc-500 mt-1 break-words">{e.user_agent}</div>
              {e.stack && (
                <pre className="mt-2 p-3 rounded-xl bg-[#0A0A0B] border border-white/5 text-[11px] text-zinc-400 overflow-x-auto max-h-40">{e.stack}</pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────── Shipping prices ───────── */
function ShippingPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/shipping-config")
      .then((r) => setCfg(r.data))
      .catch(() => toast.error("Versandeinstellungen konnten nicht geladen werden"))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setRow = (i, k, v) =>
    setCfg((c) => ({ ...c, countries: c.countries.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));
  const addCountry = () =>
    setCfg((c) => ({ ...c, countries: [...(c.countries || []), { name: "", fee: "", threshold: "", fee_above: "", active: true }] }));
  const removeCountry = (i) =>
    setCfg((c) => ({ ...c, countries: c.countries.filter((_, idx) => idx !== i) }));

  const toNum = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(String(v).replace(",", "."));
    return Number.isNaN(n) ? null : n;
  };

  const save = async () => {
    const payload = {
      fee_at: Number(String(cfg.fee_at).replace(",", ".")),
      threshold_at: Number(String(cfg.threshold_at).replace(",", ".")),
      fee_above_at: toNum(cfg.fee_above_at),
      fee_intl: Number(String(cfg.fee_intl).replace(",", ".")),
      threshold_intl: Number(String(cfg.threshold_intl).replace(",", ".")),
      fee_above_intl: toNum(cfg.fee_above_intl),
      countries: (cfg.countries || [])
        .filter((r) => String(r.name || "").trim())
        .map((r) => ({
          name: String(r.name).trim(),
          fee: toNum(r.fee),
          threshold: toNum(r.threshold),
          fee_above: toNum(r.fee_above),
          active: r.active !== false,
        })),
    };
    if ([payload.fee_at, payload.threshold_at, payload.fee_intl, payload.threshold_intl].some((v) => Number.isNaN(v) || v < 0)) {
      toast.error("Bitte nur Zahlen ab 0 eingeben");
      return;
    }
    if (payload.countries.some((c) => (c.fee !== null && c.fee < 0) || (c.fee_above !== null && c.fee_above < 0))) {
      toast.error("Länder-Versandkosten dürfen nicht negativ sein");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/shipping-config", payload);
      setCfg(r.data);
      toast.success("Versandpreise gespeichert — ab sofort in der Kasse aktiv");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>;
  }

  return (
    <div data-testid="shipping-panel" className="space-y-6">
      <p className="text-sm text-zinc-500 max-w-2xl">
        Diese Preise gelten sofort in der Kasse. Abholung im Shop ist immer gratis.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-6 rounded-2xl bg-[#141417] border border-white/5">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-zinc-300" />
            <h3 className="font-display text-lg font-semibold text-white">Österreich</h3>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3">
            <FeeField label="Versand (€)" value={cfg.fee_at} onChange={(v) => set("fee_at", v)} testid="ship-fee-at" />
            <FeeField label="Ab Bestellwert (€)" value={cfg.threshold_at} onChange={(v) => set("threshold_at", v)} testid="ship-threshold-at" />
            <FeeField
              label="Dann Versand (€)"
              value={cfg.fee_above_at ?? ""}
              onChange={(v) => set("fee_above_at", v)}
              placeholder="—"
              testid="ship-fee-above-at"
            />
          </div>
          <p className="text-[11px] text-zinc-500 mt-3">
            {cfg.fee_above_at === null || cfg.fee_above_at === ""
              ? `Immer ${cfg.fee_at} € — keine Reduktion.`
              : Number(cfg.fee_above_at) === 0
                ? `Ab ${cfg.threshold_at} € gratis.`
                : `Ab ${cfg.threshold_at} € nur ${cfg.fee_above_at} €.`}
            {" "}0 eintragen = gratis, leer = keine Reduktion.
          </p>
        </div>

        <div className="p-6 rounded-2xl bg-[#141417] border border-white/5">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#DC1928]" />
            <h3 className="font-display text-lg font-semibold text-white">Andere Länder (Standard)</h3>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3">
            <FeeField label="Versand (€)" value={cfg.fee_intl} onChange={(v) => set("fee_intl", v)} testid="ship-fee-intl" />
            <FeeField label="Ab Bestellwert (€)" value={cfg.threshold_intl} onChange={(v) => set("threshold_intl", v)} testid="ship-threshold-intl" />
            <FeeField
              label="Dann Versand (€)"
              value={cfg.fee_above_intl ?? ""}
              onChange={(v) => set("fee_above_intl", v)}
              placeholder="—"
              testid="ship-fee-above-intl"
            />
          </div>
          <p className="text-[11px] text-zinc-500 mt-3">
            {cfg.fee_above_intl === null || cfg.fee_above_intl === ""
              ? `Immer ${cfg.fee_intl} € — keine Reduktion.`
              : Number(cfg.fee_above_intl) === 0
                ? `Ab ${cfg.threshold_intl} € gratis.`
                : `Ab ${cfg.threshold_intl} € nur ${cfg.fee_above_intl} €.`}
            {" "}Gilt für jedes Land ohne eigenen Preis.
          </p>
        </div>
      </div>

      <div className="p-6 rounded-2xl bg-[#141417] border border-white/5" data-testid="ship-countries-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-lg font-semibold text-white">Lieferländer & Preise</h3>
            <p className="text-xs text-zinc-500 mt-1 max-w-xl">
              Diese Länder kann der Kunde in der Kasse auswählen. Alle Felder leer = Standard für andere
              Länder ({cfg.fee_intl} €{cfg.fee_above_intl === null ? "" : `, ab ${cfg.threshold_intl} € ${Number(cfg.fee_above_intl) === 0 ? "gratis" : `nur ${cfg.fee_above_intl} €`}`}).
            </p>
          </div>
          <Button
            onClick={addCountry}
            variant="outline"
            className="shrink-0 bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white"
            data-testid="ship-add-country"
          >
            <Plus className="w-4 h-4 mr-2" /> Land hinzufügen
          </Button>
        </div>

        <div className="mt-5 hidden md:grid grid-cols-12 gap-2 px-2 text-[10px] uppercase tracking-[0.18em] text-zinc-600 font-semibold">
          <div className="col-span-5">Land</div>
          <div className="col-span-2">Versand (€)</div>
          <div className="col-span-2">Ab Bestellwert (€)</div>
          <div className="col-span-1">Dann (€)</div>
          <div className="col-span-2 text-right">Aktiv</div>
        </div>

        <div className="mt-2 space-y-2">
          {(cfg.countries || []).map((row, i) => (
            <div
              key={i}
              className="grid grid-cols-12 gap-2 items-center p-2 rounded-xl bg-[#0A0A0B] border border-white/10"
              data-testid={`ship-country-row-${i}`}
            >
              <Input
                value={row.name || ""}
                onChange={(e) => setRow(i, "name", e.target.value)}
                placeholder="z. B. Slowakei"
                className="col-span-12 md:col-span-5 bg-[#141417] border-white/10 text-white h-10"
                data-testid={`ship-country-name-${i}`}
              />
              <Input
                value={row.fee ?? ""}
                onChange={(e) => setRow(i, "fee", e.target.value)}
                placeholder={`${cfg.fee_intl}`}
                inputMode="decimal"
                className="col-span-4 md:col-span-2 bg-[#141417] border-white/10 text-white h-10"
                data-testid={`ship-country-fee-${i}`}
              />
              <Input
                value={row.threshold ?? ""}
                onChange={(e) => setRow(i, "threshold", e.target.value)}
                placeholder={`${cfg.threshold_intl}`}
                inputMode="decimal"
                className="col-span-4 md:col-span-2 bg-[#141417] border-white/10 text-white h-10"
                data-testid={`ship-country-threshold-${i}`}
              />
              <Input
                value={row.fee_above ?? ""}
                onChange={(e) => setRow(i, "fee_above", e.target.value)}
                placeholder={cfg.fee_above_intl === null ? "—" : `${cfg.fee_above_intl}`}
                inputMode="decimal"
                className="col-span-4 md:col-span-1 bg-[#141417] border-white/10 text-white h-10"
                data-testid={`ship-country-fee-above-${i}`}
              />
              <div className="col-span-2 flex items-center justify-end gap-1">
                <button
                  type="button"
                  onClick={() => setRow(i, "active", row.active === false)}
                  className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${row.active === false ? "bg-white/10" : "bg-emerald-500"}`}
                  title={row.active === false ? "Inaktiv" : "Aktiv"}
                  data-testid={`ship-country-active-${i}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${row.active === false ? "left-0.5" : "left-[22px]"}`} />
                </button>
                <button
                  type="button"
                  onClick={() => removeCountry(i)}
                  className="p-2 rounded-lg text-zinc-500 hover:text-[#FF2D40] hover:bg-white/5 transition-colors"
                  title="Land entfernen"
                  data-testid={`ship-country-delete-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {(cfg.countries || []).length === 0 && (
            <div className="p-4 text-sm text-zinc-500" data-testid="ship-countries-empty">
              Noch keine Länder angelegt — Kunden können dann nur Österreich oder „Anderes Land" wählen.
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving} className="bg-[#DC1928] hover:bg-[#B01829] rounded-full h-11 px-6" data-testid="ship-save-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Speichern…</> : <><Check className="w-4 h-4 mr-2" /> Versandpreise speichern</>}
        </Button>
        <span className="text-xs text-zinc-500" data-testid="ship-summary">
          Aktuell: Österreich {cfg.fee_at} €
          {cfg.fee_above_at === null ? "" : Number(cfg.fee_above_at) === 0 ? ` (ab ${cfg.threshold_at} € gratis)` : ` (ab ${cfg.threshold_at} € nur ${cfg.fee_above_at} €)`}
          {" · "}Andere Länder {cfg.fee_intl} €
          {cfg.fee_above_intl === null ? "" : Number(cfg.fee_above_intl) === 0 ? ` (ab ${cfg.threshold_intl} € gratis)` : ` (ab ${cfg.threshold_intl} € nur ${cfg.fee_above_intl} €)`}
        </span>
      </div>
    </div>
  );
}

function FeeField({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold leading-tight block">{label}</Label>
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode="decimal"
        className="mt-1.5 bg-[#0A0A0B] border-white/10 text-white h-11"
        data-testid={testid}
      />
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="w-full flex items-center justify-between gap-4 p-3 rounded-xl bg-[#0A0A0B] border border-white/10 hover:border-white/20 transition-colors text-left"
      data-testid={testid}
    >
      <span className="text-sm text-zinc-300">{label}</span>
      <span className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${checked ? "bg-[#DC1928]" : "bg-white/10"}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
      </span>
    </button>
  );
}
